import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import { noteOnce } from "rc-util/es/warning";
var PARTS_CACHE_LIMIT = 512;
var formatterCache = new Map();
var validTimeZoneCache = new Map();
var configCache = new WeakMap();
var TZ_OFFSET_PATTERN = /^UTC([+-])(\d{2}):([0-5]\d)$/;
/** Parse `UTC±HH:mm` into offset minutes. */

export function parseTzOffset(tzOffset) {
  var match = TZ_OFFSET_PATTERN.exec(tzOffset);

  if (!match) {
    return null;
  }

  var hours = Number(match[2]);
  var minutes = Number(match[3]);

  if (hours > 14 || hours === 14 && minutes !== 0) {
    return null;
  }

  var absoluteMinutes = hours * 60 + minutes;
  return match[1] === '-' ? -absoluteMinutes : absoluteMinutes;
}
export function isValidTzOffset(tzOffset) {
  return !tzOffset || parseTzOffset(tzOffset) !== null;
}

function getFormatter(timeZone) {
  var formatter = formatterCache.get(timeZone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    formatterCache.set(timeZone, formatter);
  }

  return formatter;
}

export function isValidTimeZone(timeZone) {
  if (!timeZone) {
    return true;
  }

  var cached = validTimeZoneCache.get(timeZone);

  if (cached !== undefined) {
    return cached;
  }

  var valid = true;

  try {
    getFormatter(timeZone).format(0);
  } catch (error) {
    valid = false;
    formatterCache.delete(timeZone);
  }

  validTimeZoneCache.set(timeZone, valid);
  return valid;
}

function toUtcTimestamp(parts) {
  var date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month, parts.date);
  date.setUTCHours(parts.hour, parts.minute, parts.second, parts.millisecond);
  return date.getTime();
}

function toLocalTimestamp(parts) {
  var date = new Date(0);
  date.setFullYear(parts.year, parts.month, parts.date);
  date.setHours(parts.hour, parts.minute, parts.second, parts.millisecond);
  return date.getTime();
}

function daysInMonth(year, month) {
  var date = new Date(0);
  date.setUTCFullYear(year, month + 1, 0);
  return date.getUTCDate();
}

function sameParts(left, right) {
  return left.year === right.year && left.month === right.month && left.date === right.date && left.hour === right.hour && left.minute === right.minute && left.second === right.second && left.millisecond === right.millisecond;
}

function normalizeParts(parts) {
  var date = new Date(toUtcTimestamp(parts));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    date: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
    millisecond: date.getUTCMilliseconds()
  };
}

function readUtcParts(timestamp) {
  var date = new Date(timestamp);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    date: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
    millisecond: date.getUTCMilliseconds()
  };
}

function createOffsetPartsReader(offsetMinutes) {
  var offsetMilliseconds = offsetMinutes * 60 * 1000;
  var cache = new Map();
  return function (timestamp) {
    var cached = cache.get(timestamp);

    if (cached) {
      return cached;
    }

    var parts = readUtcParts(timestamp + offsetMilliseconds);
    cache.set(timestamp, parts);

    if (cache.size > PARTS_CACHE_LIMIT) {
      cache.delete(cache.keys().next().value);
    }

    return parts;
  };
}

function createPartsReader(timeZone) {
  var formatter = getFormatter(timeZone);
  var cache = new Map();
  return function (timestamp) {
    var cached = cache.get(timestamp);

    if (cached) {
      return cached;
    }

    var values = {};
    formatter.formatToParts(new Date(timestamp)).forEach(function (part) {
      if (part.type !== 'literal') {
        values[part.type] = Number(part.value);
      }
    });
    var parts = {
      year: values.year,
      month: values.month - 1,
      date: values.day,
      hour: values.hour === 24 ? 0 : values.hour,
      minute: values.minute,
      second: values.second,
      millisecond: (timestamp % 1000 + 1000) % 1000
    };
    cache.set(timestamp, parts);

    if (cache.size > PARTS_CACHE_LIMIT) {
      cache.delete(cache.keys().next().value);
    }

    return parts;
  };
}
/**
 * 将通用日期适配器绑定到指定 IANA 时区或固定 UTC 偏移量，不改变值的类型。
 * 值仍为绝对时刻；仅日历字段的读取与日历运算会投影到目标时区/偏移量。
 */


export function createTimeZoneConfig(generateConfig, timeZone, tzOffset) {
  var offsetMinutes = null;

  if (tzOffset) {
    offsetMinutes = parseTzOffset(tzOffset);

    if (offsetMinutes === null) {
      noteOnce(false, "Invalid time-zone offset `".concat(tzOffset, "`. Expected `UTC\xB1HH:mm`; falling back to `timeZone`."));
    }
  }

  var useOffset = offsetMinutes !== null;

  if (!useOffset && !timeZone) {
    return generateConfig;
  }

  if (!useOffset && !isValidTimeZone(timeZone)) {
    noteOnce(false, "Invalid IANA time zone `".concat(timeZone, "`. Falling back to the adapter time zone."));
    return generateConfig;
  }

  if (!generateConfig.getTimestamp || !generateConfig.fromTimestamp) {
    noteOnce(false, '`timeZone` and `tzOffset` require generateConfig.getTimestamp and generateConfig.fromTimestamp. Falling back to the adapter time zone.');
    return generateConfig;
  }

  var cachedByZone = configCache.get(generateConfig);

  if (!cachedByZone) {
    cachedByZone = new Map();
    configCache.set(generateConfig, cachedByZone);
  }

  var calculationKey = useOffset ? "offset:".concat(offsetMinutes) : "zone:".concat(timeZone);
  var cachedConfig = cachedByZone.get(calculationKey);

  if (cachedConfig) {
    return cachedConfig;
  }

  var getTimestamp = generateConfig.getTimestamp;
  var fromTimestamp = generateConfig.fromTimestamp;
  var readParts = useOffset ? createOffsetPartsReader(offsetMinutes) : createPartsReader(timeZone);

  var partsOf = function partsOf(value) {
    return readParts(getTimestamp(value));
  };

  var getOffset = function getOffset(timestamp) {
    var roundedTimestamp = Math.floor(timestamp / 1000) * 1000;
    return toUtcTimestamp(readParts(roundedTimestamp)) - roundedTimestamp;
  };

  var fromIanaParts = function fromIanaParts(input) {
    var parts = normalizeParts(input);
    var target = toUtcTimestamp(parts);
    var initialOffset = getOffset(target);
    var initialCandidate = target - initialOffset;
    var offsets = [initialOffset, getOffset(initialCandidate - 18 * 60 * 60 * 1000), getOffset(initialCandidate + 18 * 60 * 60 * 1000)];
    var candidates = offsets.filter(function (offset, index) {
      return offsets.indexOf(offset) === index;
    }).map(function (offset) {
      return target - offset;
    }).filter(function (timestamp) {
      return sameParts(readParts(timestamp), parts);
    }).sort(function (left, right) {
      return left - right;
    });

    if (candidates.length) {
      // 夏令时重叠时段（时钟回拨），取较早的那一次。
      return fromTimestamp(candidates[0]);
    } // 夏令时空档时段（时钟拨快），使用过渡前的偏移量。
    // 这会将不存在的本地时间按空档大小向前推移（与 Temporal 行为一致）。


    var beforeOffset = getOffset(initialCandidate - 36 * 60 * 60 * 1000);
    return fromTimestamp(target - beforeOffset);
  };

  var fromParts = useOffset ? function (input) {
    return fromTimestamp(toUtcTimestamp(normalizeParts(input)) - offsetMinutes * 60 * 1000);
  } : fromIanaParts;

  var projectToAdapterZone = function projectToAdapterZone(value) {
    return fromTimestamp(toLocalTimestamp(partsOf(value)));
  };

  var adapterValueToParts = function adapterValueToParts(value) {
    return {
      year: generateConfig.getYear(value),
      month: generateConfig.getMonth(value),
      date: generateConfig.getDate(value),
      hour: generateConfig.getHour(value),
      minute: generateConfig.getMinute(value),
      second: generateConfig.getSecond(value),
      millisecond: 0
    };
  };

  var update = function update(value, updater) {
    return fromParts(updater(partsOf(value)));
  };

  var setClampedYearMonth = function setClampedYearMonth(parts, year, month) {
    return _objectSpread(_objectSpread({}, parts), {}, {
      year: year,
      month: month,
      date: Math.min(parts.date, daysInMonth(year, month))
    });
  };

  var boundConfig = {
    getTimestamp: getTimestamp,
    fromTimestamp: fromTimestamp,
    getNow: function getNow() {
      return fromTimestamp(Date.now());
    },
    getFixedDate: function getFixedDate(fixed) {
      var match = /^(\d{4,})-(\d{1,2})-(\d{1,2})$/.exec(fixed);

      if (match) {
        return fromParts({
          year: Number(match[1]),
          month: Number(match[2]) - 1,
          date: Number(match[3]),
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        });
      }

      return fromParts(adapterValueToParts(generateConfig.getFixedDate(fixed)));
    },
    getEndDate: function getEndDate(value) {
      var parts = partsOf(value);
      return fromParts(_objectSpread(_objectSpread({}, parts), {}, {
        date: daysInMonth(parts.year, parts.month),
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
      }));
    },
    getWeekDay: function getWeekDay(value) {
      var parts = partsOf(value);
      return new Date(toUtcTimestamp(_objectSpread(_objectSpread({}, parts), {}, {
        hour: 0,
        minute: 0,
        second: 0
      }))).getUTCDay();
    },
    getYear: function getYear(value) {
      return partsOf(value).year;
    },
    getMonth: function getMonth(value) {
      return partsOf(value).month;
    },
    getDate: function getDate(value) {
      return partsOf(value).date;
    },
    getHour: function getHour(value) {
      return partsOf(value).hour;
    },
    getMinute: function getMinute(value) {
      return partsOf(value).minute;
    },
    getSecond: function getSecond(value) {
      return partsOf(value).second;
    },
    addYear: function addYear(value, diff) {
      return update(value, function (parts) {
        return setClampedYearMonth(parts, parts.year + diff, parts.month);
      });
    },
    addMonth: function addMonth(value, diff) {
      return update(value, function (parts) {
        var monthDate = new Date(0);
        monthDate.setUTCFullYear(parts.year, parts.month + diff, 1);
        return setClampedYearMonth(parts, monthDate.getUTCFullYear(), monthDate.getUTCMonth());
      });
    },
    addDate: function addDate(value, diff) {
      return update(value, function (parts) {
        return normalizeParts(_objectSpread(_objectSpread({}, parts), {}, {
          date: parts.date + diff
        }));
      });
    },
    setYear: function setYear(value, year) {
      return update(value, function (parts) {
        return setClampedYearMonth(parts, year, parts.month);
      });
    },
    setMonth: function setMonth(value, month) {
      return update(value, function (parts) {
        var monthDate = new Date(0);
        monthDate.setUTCFullYear(parts.year, month, 1);
        return setClampedYearMonth(parts, monthDate.getUTCFullYear(), monthDate.getUTCMonth());
      });
    },
    setDate: function setDate(value, date) {
      return update(value, function (parts) {
        return normalizeParts(_objectSpread(_objectSpread({}, parts), {}, {
          date: date
        }));
      });
    },
    setHour: function setHour(value, hour) {
      return update(value, function (parts) {
        return normalizeParts(_objectSpread(_objectSpread({}, parts), {}, {
          hour: hour
        }));
      });
    },
    setMinute: function setMinute(value, minute) {
      return update(value, function (parts) {
        return normalizeParts(_objectSpread(_objectSpread({}, parts), {}, {
          minute: minute
        }));
      });
    },
    setSecond: function setSecond(value, second) {
      return update(value, function (parts) {
        return normalizeParts(_objectSpread(_objectSpread({}, parts), {}, {
          second: second
        }));
      });
    },
    // 适配器的比较已基于绝对时刻。委托给原适配器还能保留其对
    // RangePicker 悬停预览时临时半填值的运行时容错。
    isAfter: function isAfter(date1, date2) {
      return generateConfig.isAfter(date1, date2);
    },
    isValidate: function isValidate(date) {
      return generateConfig.isValidate(date) && !Number.isNaN(getTimestamp(date));
    },
    locale: _objectSpread(_objectSpread({}, generateConfig.locale), {}, {
      getWeekFirstDate: function getWeekFirstDate(locale, value) {
        return fromParts(adapterValueToParts(generateConfig.locale.getWeekFirstDate(locale, projectToAdapterZone(value))));
      },
      getWeek: function getWeek(locale, value) {
        return generateConfig.locale.getWeek(locale, projectToAdapterZone(value));
      },
      format: function format(locale, value, _format) {
        return generateConfig.locale.format(locale, projectToAdapterZone(value), _format);
      },
      parse: function parse(locale, text, formats) {
        var parsed = generateConfig.locale.parse(locale, text, formats);
        return parsed ? fromParts(adapterValueToParts(parsed)) : null;
      }
    })
  };
  cachedByZone.set(calculationKey, boundConfig);
  var nestedCache = configCache.get(boundConfig);

  if (!nestedCache) {
    nestedCache = new Map();
    configCache.set(boundConfig, nestedCache);
  }

  nestedCache.set(calculationKey, boundConfig);
  return boundConfig;
}